import { Button, Popover, List, ListItem, Drawer, Box, styled, Tooltip } from "@mui/material";
import { DrawerHeader, Ico_Plus } from "@sagecentral/core-ui";
import { useEffect, useRef, useState } from "react";
import TaskAttachment from "./TaskAttachment";
import TaskExistingAttachment from "../addExisting/TaskExistingAttachment";
import { useTaskContext } from "hooks/tasks";
import { IAttachmentTypeListItem, IFileAttachment } from "models/attachments";

type TaskAttachmentDrawerProps = {
    setRefreshGrid: React.Dispatch<React.SetStateAction<boolean>>;
    refreshGrid: boolean;
    attachments: IFileAttachment[];
    types: IAttachmentTypeListItem[] | null;
};

const AttachmentBox = styled(Box)(({ theme }) => ({
    padding: '20px 20px',
    margin: '10px 10px',
    maxWidth: '84vw',
    minWidth: '30vw',
    fontFamily: 'Open Sans',
    '& .header': {
        borderBottom: '2px solid #001A69 !important',
        color: '#555555',
        paddingRight: '30px'
    },
    '& .row': {
        borderBottom: 'none !important',
        minHeight: '52px !important',
        '&:hover': {
            backgroundColor: '#D9EEFF !important',
        },
    },
    '& .odd': {
        backgroundColor: '#F7F7F7',
    },
    '& .even': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-cell': {
        borderBottom: 'none !important',
        paddingRight: '30px'
    },
    '& .MuiDataGrid-sortIcon': {
        fontSize: '24px'
    },
}));

const TaskAttachmentDrawer = (props: TaskAttachmentDrawerProps) => {
    const { types } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [openAddExisting, setOpenAddExisting] = useState(false);
    const [openAddNew, setOpenAddNew] = useState(false);
    const [addedExisting, setAddedExisting] = useState(false);
    const taskContext = useTaskContext();
    const { task } = taskContext;
    const open = Boolean(anchorEl);

    const addIsDisabled = props.types == null || (props.types != null && props.types.length == 0);

    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setTimeout(() => {
            if (buttonRef.current) {
                buttonRef.current.blur();
            }
        }, 50);
    };

    /**
     * Closes the drawer and returns focus to the Add button
     */
    const closeDrawerAndClearFocus = (setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>) => {
        setOpenDrawer(false);
        setAnchorEl(null);

        // Return focus to the Add button after drawer closes
        setTimeout(() => {
            if (buttonRef.current) {
                buttonRef.current.focus();
            }
        }, 50);
    };

    useEffect(() => {
        if (addedExisting) {
            setOpenAddExisting(false);
            props.setRefreshGrid(!props.refreshGrid);
            setAddedExisting(false);
        }
    }, [addedExisting]);

    const button = (
        <span>
            <Button
                ref={buttonRef}
                variant="contained"
                sx={{
                    width: '212px',
                    height: '40px',
                    margin: '0px',
                }}
                onClick={handleClick}
                id="add-attachment-button"
                disabled={addIsDisabled}
                startIcon={<Ico_Plus />}
            >
                Add
            </Button>
        </span>
    );

    return (
        <>
            {addIsDisabled ? (
                <Tooltip title={'No attachments are required for this task.'}>
                    {button}
                </Tooltip>
            ) : (
                button
            )}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableRestoreFocus
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}>
                <List>
                    <ListItem>
                        <Button onClick={() => setOpenAddNew(true)}>New Attachment</Button>
                        <Drawer
                            anchor={'right'}
                            open={openAddNew}
                            onClose={() => closeDrawerAndClearFocus(setOpenAddNew)}
                            PaperProps={{
                                sx: {
                                    width: '50%',
                                    backgroundColor: "rgba(255, 255, 255, 1)"
                                }
                            }}
                            sx={{ zIndex: 1300 }}
                            disableRestoreFocus
                        >
                            <DrawerHeader
                                title="Add New Attachment"
                                onClose={() => closeDrawerAndClearFocus(setOpenAddNew)}
                            />
                            <TaskAttachment
                                types={types}
                                onClose={() => closeDrawerAndClearFocus(setOpenAddNew)}
                                onRefresh={() => props.setRefreshGrid(!props.refreshGrid)}
                                attachments={props.attachments}
                            />
                        </Drawer>
                    </ListItem>
                    <ListItem>
                        <Button onClick={() => setOpenAddExisting(true)}>Existing Attachment</Button>
                        <Drawer
                            anchor={'right'}
                            open={openAddExisting}
                            onClose={() => closeDrawerAndClearFocus(setOpenAddExisting)}
                            PaperProps={{
                                sx: {
                                    width: '70%',
                                    backgroundColor: "rgba(255, 255, 255, 1)"
                                }
                            }}
                            sx={{ zIndex: 1300 }}
                            disableRestoreFocus
                        >
                            <DrawerHeader
                                title="Search Existing Attachment"
                                onClose={() => closeDrawerAndClearFocus(setOpenAddExisting)}
                            />
                            <TaskExistingAttachment
                                types={types}
                                onClose={() => closeDrawerAndClearFocus(setOpenAddExisting)}
                                onRefresh={() => props.setRefreshGrid(!props.refreshGrid)}
                                taskId={task?.id}
                            />
                        </Drawer>
                    </ListItem>
                </List>
            </Popover>
        </>
    );
}

export default TaskAttachmentDrawer;